import { Headline6 } from '@/app/components/Typography'
import tailwindConfig from '@/tailwind.config'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Head from 'next/head'
import { useEffect, useRef, useState } from 'react'
import resolveConfig from 'tailwindcss/resolveConfig'
import { ImageWithFallback } from './image-with-fallback'

const DISABLE_ANIMATION = '--provider-ticker-disable-animation'

type Provider = {
  id: string
  name: string
  logo: string
}

export type TopProvidersSectionProps = {
  /** The headline to display */
  headline?: string
  /** The providers to display */
  providers: Provider[]
  /** The variant of the section - "ticker" animates on desktop, "static" shows a fixed grid */
  variant?: 'ticker' | 'static'
} & React.HTMLAttributes<HTMLDivElement>

export function TopProvidersSection({
  headline,
  providers,
  variant = 'ticker',
  ...props
}: TopProvidersSectionProps) {
  const t = useTranslations('TopProvidersSection')

  return (
    <section
      {...props}
      className="TopProvidersSection flex flex-col gap-8 bg-mydra-black pb-11 pt-9 text-white md:gap-12 md:pb-28 md:pt-16"
    >
      {headline && (
        <header className="container flex flex-col items-center justify-center gap-2 text-center">
          {headline && (
            <Headline6 as="h2" className="!text-2xl !font-normal">
              {headline ?? t('headline')}
            </Headline6>
          )}
        </header>
      )}
      <div className="container">
        {variant === 'ticker' ? (
          <ProviderTicker providers={providers} />
        ) : (
          <ProviderGrid providers={providers} className="mx-auto" />
        )}
      </div>
    </section>
  )
}

type ProviderTickerProps = {
  providers: Provider[]
  bgColor?: string
} & React.HTMLAttributes<HTMLDivElement>

function ProviderTicker({ providers, ...props }: ProviderTickerProps) {
  const [shouldAnimate, setShouldAnimate] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLUListElement>(null)
  const { theme } = resolveConfig(tailwindConfig)
  const mdScreen = theme?.screens?.['md']

  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current) return

      const containerWidth = containerRef.current.offsetWidth
      const shouldDisableAnimation = getComputedStyle(
        document.documentElement
      ).getPropertyValue(DISABLE_ANIMATION)

      if (parseInt(shouldDisableAnimation)) {
        return setShouldAnimate(false)
      }

      if (containerRef.current && contentRef.current) {
        const totalContentWidth = contentRef.current.offsetWidth * 2
        setShouldAnimate(totalContentWidth > containerWidth)
      }
    }

    checkOverflow()
    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [providers, theme?.screens])

  if (!Array.isArray(providers) || providers.length === 0) {
    return null
  }

  return (
    <>
      <Head>
        <style>
          {`
            @media (max-width: calc(${mdScreen} - 1px)) {
              :root {
                ${DISABLE_ANIMATION}: 1;
              }
            }
          `}
        </style>
      </Head>
      <div className="relative">
        <div
          className="absolute left-0 top-0 z-10 h-full w-16"
          style={{
            background: `linear-gradient(to right, #111111 0%, transparent 100%)`,
            opacity: shouldAnimate ? 1 : 0,
          }}
        ></div>
        <div
          {...props}
          ref={containerRef}
          className="relative flex w-full overflow-hidden"
        >
          <motion.div
            animate={
              shouldAnimate
                ? {
                    x: ['0%', '-50%'],
                  }
                : undefined
            }
            transition={{
              duration: 20,
              repeat: shouldAnimate ? Infinity : 0,
              ease: 'linear',
              transitionEnd: { display: 'none' },
            }}
            className="mx-auto flex whitespace-nowrap max-md:!transform-none md:mx-0 md:transform"
          >
            <ul
              ref={contentRef}
              className="mx-auto grid grid-cols-2 items-baseline gap-x-2 gap-y-4 md:flex"
            >
              {providers.map((provider) => (
                <ProviderItem key={provider.id} provider={provider} />
              ))}
            </ul>
            {shouldAnimate && (
              <ul className="mx-2 flex gap-2">
                {providers.map((provider) => (
                  <ProviderItem
                    key={`duplicate-${provider.id}`}
                    provider={provider}
                  />
                ))}
              </ul>
            )}
          </motion.div>
        </div>
        <div
          className="absolute right-0 top-0 h-full w-16"
          style={{
            background: `linear-gradient(to right, transparent 0%, #111111 100%)`,
            opacity: shouldAnimate ? 1 : 0,
          }}
        ></div>
      </div>
    </>
  )
}

type ProviderGridProps = {
  providers: Provider[]
} & React.HTMLAttributes<HTMLDivElement>

function ProviderGrid({ providers, ...props }: ProviderGridProps) {
  if (!Array.isArray(providers) || providers.length === 0) {
    return null
  }

  return (
    <div {...props} className={`flex justify-center ${props.className ?? ''}`}>
      <div className="flex w-full max-w-[1567px] flex-wrap justify-center gap-4 md:gap-8">
        {providers.map((provider) => (
          <ProviderItem key={provider.id} provider={provider} isStatic />
        ))}
      </div>
    </div>
  )
}

type ProviderItemProps = {
  provider: Provider
  isStatic?: boolean
} & React.HTMLAttributes<HTMLLIElement>

function ProviderItem({ provider, isStatic, ...props }: ProviderItemProps) {
  if (isStatic) {
    return (
      <li
        {...props}
        className={`relative block h-[95px] w-full overflow-hidden rounded-lg bg-white/10 sm:w-[calc(50%-0.5rem)] md:h-[132px] md:w-[calc(25%-1.5rem)] lg:w-[calc(16.666%-1.666rem)] ${props.className ?? ''}`}
      >
        <ImageWithFallback
          src={provider.logo}
          alt={provider.name}
          fill
          className="object-contain p-6 filter"
          sizes="(max-width: 768px) 172px, 234px"
          fallback={
            <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
              <p className="text-wrap p-4 text-center text-white/50">
                {provider.name}
              </p>
            </div>
          }
        />
      </li>
    )
  }

  return (
    <li
      {...props}
      className={`relative inline-block aspect-square overflow-hidden whitespace-nowrap rounded-lg bg-white/10 p-4 md:aspect-auto ${props.className ?? ''}`}
    >
      <div className="relative h-full w-full md:h-[132px] md:w-[234px]">
        <ImageWithFallback
          src={provider.logo}
          alt={provider.name}
          fill
          className="object-contain p-4 contrast-50 saturate-0 filter md:p-0"
          sizes="234px"
          fallback={
            <div className="absolute left-0 top-0 flex h-full w-full items-center justify-center">
              <p className="text-wrap p-4 text-center text-white/50">
                {provider.name}
              </p>
            </div>
          }
        />
      </div>
    </li>
  )
}
